import { BentoGrid } from '@/components/bloks/bento-grid'
import { Community } from '@/components/bloks/community'
import { CompanyLogos } from '@/components/bloks/company-logos'
import { ComparisonSlider } from '@/components/bloks/comparison-slider'
import { ContactFormSection } from '@/components/bloks/contact-form'
import { ContentMedia } from '@/components/bloks/content-media'
import { Faq } from '@/components/bloks/faq'
import { FeatureMatrix } from '@/components/bloks/feature-matrix'
import { FeatureTeaserLarge } from '@/components/bloks/feature-teaser-large'
import { FeaturedPartner } from '@/components/bloks/featured-partner'
import { FoundationalBlocks } from '@/components/bloks/foundational-blocks'
import { IncludeSnippet } from '@/components/bloks/include-snippet'
import { MainHero } from '@/components/bloks/main-hero'
import { Page } from '@/components/bloks/page'
import { PageHeader } from '@/components/bloks/page-header'
import { PageHero } from '@/components/bloks/page-hero'
import { PartnerFormSection } from '@/components/bloks/partner-form'
import { PartnerWaitlistFormSection } from '@/components/bloks/partner-waitlist-form'
import { PricingTable } from '@/components/bloks/pricing-table'
import { SimpleText } from '@/components/bloks/simple-text'
import { Snippet } from '@/components/bloks/snippet'
import { Testimonials } from '@/components/bloks/testimonials'
import { VideoEmbed } from '@/components/bloks/video-embed'
import { ISbStoriesParams } from '@storyblok/react'
import { apiPlugin, ISbStoryData, storyblokInit } from '@storyblok/react/rsc'
import type { ISbCustomFetch } from 'storyblok-js-client'
import { logger } from './logger'
import { tryCatch } from './try-catch'

export const getStoryblokApi = storyblokInit({
  accessToken: process.env.STORYBLOK_PREVIEW_TOKEN ?? process.env.NEXT_PUBLIC_STORYBLOK_READ_TOKEN,
  use: [apiPlugin],
  components: {
    page: Page,
    'main-hero': MainHero,
    'company-logos': CompanyLogos,
    'bento-grid': BentoGrid,
    'feature-matrix': FeatureMatrix,
    'feature-teaser-large': FeatureTeaserLarge,
    'content-media': ContentMedia,
    testimonials: Testimonials,
    community: Community,
    'page-hero': PageHero,
    'comparison-slider': ComparisonSlider,
    'foundational-blocks': FoundationalBlocks,
    'page-header': PageHeader,
    'pricing-table': PricingTable,
    faq: Faq,
    'simple-text': SimpleText,
    snippet: Snippet,
    'include-snippet': IncludeSnippet,
    'contact-form': ContactFormSection,
    'partner-form': PartnerFormSection,
    'partner-waitlist': PartnerWaitlistFormSection,
    'featured-partner': FeaturedPartner,
    video: VideoEmbed,
  },
})

export function getFetchVersion(): ISbStoriesParams['version'] {
  if (process.env.NEXT_PUBLIC_FORCE_DRAFT_VERSION === 'true') {
    return 'draft'
  }

  if (process.env.APP_ENV === 'development') {
    return 'draft'
  }

  return 'published'
}

export async function getStory<T extends { _uid: string; component: string }>(
  slug: string,
  resolveRelations: string[] = [],
  forceDraft = false,
  fetchOptions: ISbCustomFetch | undefined = undefined,
  byUuid = false,
) {
  const storyblokApi = getStoryblokApi()
  const fetchVersion = forceDraft ? 'draft' : getFetchVersion()

  // Apply default cache options for published content
  const defaultFetchOptions: ISbCustomFetch =
    fetchVersion === 'published' && !fetchOptions
      ? {
          cache: 'force-cache',
          next: { revalidate: 86400 }, // 24 hours
        }
      : {}

  const { data: res, error } = await tryCatch(
    storyblokApi.getStory(
      slug.replace(/^\/+/, ''),
      {
        version: fetchVersion,
        resolve_relations: resolveRelations,
        ...(byUuid ? { find_by: 'uuid' } : {}),
      },
      fetchOptions || defaultFetchOptions,
    ),
  )

  if (error) {
    logger.error({ error, slug }, 'Story could not be fetched')
    throw error
  }

  return res.data.story as ISbStoryData<T>
}

export async function getDataSourceEntries(
  id: string,
  dimension: string | undefined = undefined,
): Promise<{ name: string; value: string }[]> {
  const storyblokApi = getStoryblokApi()

  const { data } = await storyblokApi.get('cdn/datasource_entries', {
    datasource: id,
    dimension,
  })

  return data.datasource_entries.map((item: any) => {
    return {
      name: item.name,
      value: dimension ? item.dimension_value : item.value,
    }
  })
}
